import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import type { GarmentInsight, InsightDistribution, WardrobeInsights } from '../../shared/insights';
import { getWardrobeInsights } from '../lib/insights';
import { Icon } from './Icon';

function formatLastWorn(value: string | null): string {
  if (!value) return 'Not worn yet';
  return `Last worn ${new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T12:00:00`))}`;
}

function GarmentInsightList({ items, empty }: { items: GarmentInsight[]; empty: string }) {
  if (!items.length) return <p className="insights-empty-copy">{empty}</p>;
  return (
    <div className="insight-garment-list">
      {items.map((item) => (
        <NavLink key={item.garment.id} to={`/closet/${item.garment.id}`}>
          <img alt="" src={item.garment.photoUrl} />
          <span>
            <strong>{item.garment.name}</strong>
            <small>{formatLastWorn(item.lastWorn)}</small>
          </span>
          <b>
            {item.wearCount} wear{item.wearCount === 1 ? '' : 's'}
          </b>
        </NavLink>
      ))}
    </div>
  );
}

function DistributionChart({
  title,
  values,
  empty,
}: {
  title: string;
  values: InsightDistribution[];
  empty: string;
}) {
  return (
    <section className="insight-panel">
      <div className="insight-panel__heading">
        <div>
          <p className="eyebrow">Your mix</p>
          <h2>{title}</h2>
        </div>
      </div>
      {values.length ? (
        <div className="distribution-list">
          {values.slice(0, 8).map((value) => (
            <div key={value.label}>
              <span>
                <strong>{value.label}</strong>
                <small>
                  {value.count} · {value.percentage}%
                </small>
              </span>
              <i aria-hidden="true">
                <b style={{ width: `${value.percentage}%` }} />
              </i>
            </div>
          ))}
        </div>
      ) : (
        <p className="insights-empty-copy">{empty}</p>
      )}
    </section>
  );
}

function MetricCard({ label, value, note }: { label: string; value: number; note: string }) {
  return (
    <article className="metric-card">
      <p>{label}</p>
      <strong>{value}</strong>
      <span>{note}</span>
    </article>
  );
}

export function InsightsPage() {
  const [insights, setInsights] = useState<WardrobeInsights | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    void getWardrobeInsights()
      .then(setInsights)
      .catch((caught: unknown) =>
        setMessage(caught instanceof Error ? caught.message : 'Your insights could not load.'),
      );
  }, []);

  if (message) {
    return (
      <div className="page">
        <header className="page-header">
          <div>
            <p className="eyebrow">Wardrobe patterns</p>
            <h1>Insights</h1>
            <p>{message}</p>
          </div>
        </header>
      </div>
    );
  }
  if (!insights)
    return (
      <div className="page">
        <p>Reading your wardrobe patterns…</p>
      </div>
    );

  if (insights.totalPieces === 0) {
    return (
      <div className="page">
        <header className="page-header">
          <div>
            <p className="eyebrow">Wardrobe patterns</p>
            <h1>Insights</h1>
            <p>A kinder look at what you reach for most.</p>
          </div>
        </header>
        <section className="empty-state">
          <span className="empty-state__icon">
            <Icon name="chart" size={34} />
          </span>
          <p className="eyebrow">A fresh beginning</p>
          <h2>Your patterns will grow naturally</h2>
          <p>Add your real clothes first. Nothing here will be invented or guessed.</p>
          <NavLink className="button button--primary" to="/add">
            Add a garment
          </NavLink>
        </section>
      </div>
    );
  }

  const noWearHistory = insights.totalWears === 0;
  return (
    <div className="page insights-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Wardrobe patterns</p>
          <h1>Insights</h1>
          <p>Real patterns from your closet and the outfits you record wearing.</p>
        </div>
        <NavLink className="button button--secondary" to="/today">
          Choose today’s look
        </NavLink>
      </header>

      <div className="insight-grid insight-grid--live">
        <MetricCard label="Closet pieces" value={insights.totalPieces} note="Active garments" />
        <MetricCard label="Times worn" value={insights.totalWears} note="From wear history" />
        <MetricCard label="Pieces explored" value={insights.wornPieces} note="Worn at least once" />
        <MetricCard label="Saved outfits" value={insights.savedOutfits} note="Looks you created" />
      </div>

      {noWearHistory ? (
        <section className="insight-gentle-start">
          <span>
            <Icon name="heart" size={24} />
          </span>
          <div>
            <h2>Your closet is ready; your history is just beginning</h2>
            <p>
              Record a look from Today when you wear it. Most worn and last worn will then appear
              from real choices—not guesses.
            </p>
          </div>
        </section>
      ) : null}

      <div className="insight-two-column">
        <section className="insight-panel">
          <div className="insight-panel__heading">
            <div>
              <p className="eyebrow">Real favourites</p>
              <h2>Most worn</h2>
            </div>
          </div>
          <GarmentInsightList
            empty="Record a worn outfit and your most-loved pieces will appear here."
            items={insights.mostWorn}
          />
        </section>
        <section className="insight-panel">
          <div className="insight-panel__heading">
            <div>
              <p className="eyebrow">Try something different</p>
              <h2>Least worn</h2>
            </div>
          </div>
          <GarmentInsightList
            empty="Add garments to see pieces waiting for their turn."
            items={insights.leastWorn}
          />
        </section>
      </div>

      <div className="insight-two-column">
        <DistributionChart
          empty="Categories appear as you add garments."
          title="By category"
          values={insights.categoryDistribution}
        />
        <DistributionChart
          empty="Colours appear as you add garments."
          title="By colour"
          values={insights.colorDistribution}
        />
      </div>

      <section className="insight-panel rediscovery-panel">
        <div className="insight-panel__heading">
          <div>
            <p className="eyebrow">From your own closet</p>
            <h2>Worth rediscovering</h2>
            <p>Unworn pieces and pieces not recorded in the last 30 days.</p>
          </div>
          <NavLink className="text-button" to="/style">
            Style a piece
          </NavLink>
        </div>
        <GarmentInsightList
          empty="You have recently worn everything—beautifully balanced."
          items={insights.rediscover}
        />
      </section>

      {insights.favorites.length ? (
        <section className="insight-panel">
          <div className="insight-panel__heading">
            <div>
              <p className="eyebrow">{insights.favoritePieces} saved favourites</p>
              <h2>Your favourites</h2>
            </div>
          </div>
          <GarmentInsightList empty="" items={insights.favorites} />
        </section>
      ) : null}
    </div>
  );
}
